// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

/**
 * Client (pet-owner) contact edits.
 *
 * These do NOT go through the dataProvider. Two reasons, both load-bearing:
 *
 *  1. The pet Edit form's control is nested under `owner.name`, and letting it
 *     ride the pets PATCH would send `owner: { … }` — a RELATIONSHIP write.
 *     Payload would reconcile the relationship id and silently drop the name,
 *     so AutoSave would flash "Saved" over a discarded edit (the exact L-029
 *     failure the rest of PetEdit is written to avoid).
 *  2. `Users.access.update` still denies vets, deliberately. The backend
 *     exposes a whitelist endpoint instead (`PATCH /api/users/client-profile`,
 *     ada-core `src/collections/Users/clientProfile.ts`) that accepts ONLY
 *     name + phone, so email/role/accountStatus stay unreachable.
 */

export interface ClientProfilePatch {
  ownerId: number | string
  name?: string
  phone?: string
}

export interface ClientProfileDoc {
  id: number | string
  name: string | null
  phone: string | null
}

export type ClientProfileResult =
  | { ok: true; data: ClientProfileDoc }
  | { ok: false; status: number; error: string }

/** Roles the SERVER accepts on /client-profile — mirror of hasVetRole + admin. */
const CLIENT_EDIT_ROLES = ['vet', 'vetadmin', 'admin']

/**
 * Whether this role may edit client contact details. Mirrors the server gate so
 * the portal disables the control rather than offering an edit that 403s — the
 * same mirror-the-server rule petFieldAccess.ts documents.
 *
 * ⚠️ DRIFT WARNING: source of truth is ada-core `clientProfile.ts`
 * (`hasVetRole(user.role) || hasAdminRole(user.role)`). Re-check on any change
 * there. `clientProfile.test.ts` pins the expected set.
 */
export const canEditClientProfile = (role?: string): boolean =>
  !!role && CLIENT_EDIT_ROLES.includes(role)

export async function updateClientProfile(patch: ClientProfilePatch): Promise<ClientProfileResult> {
  let response: Response
  try {
    response = await deadlineFetch('/api/users/client-profile', {
      method: 'PATCH',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(patch),
    })
  } catch (_e) {
    return { ok: false, status: 0, error: 'network' }
  }

  const body = (await response.json().catch(() => ({}))) as {
    success?: boolean
    data?: ClientProfileDoc
    error?: unknown
  }
  if (response.ok && body.success && body.data) {
    return { ok: true, data: body.data }
  }
  return { ok: false, status: response.status, error: String(body.error ?? 'unknown') }
}
